import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ComponentProps } from 'react';

import { cn } from '@/shared/lib/cn';

import { Dots } from './loader';

/**
 * TDS button — 사이즈와 라운드·라벨이 짝으로 움직인다 (XL 56/16, L 48/14, M 40/12, S 32/10).
 * pressed 는 fill 위에 검정 26% overlay, disabled 는 컴포넌트 전체에 opacity 0.3 (부분 회색 처리 금지).
 * primary(토스 블루)는 화면당 하나의 가장 중요한 액션에만 쓴다.
 */
const buttonVariants = cva(
  [
    'relative isolate inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap select-none',
    'transition-[background-color,color,opacity] duration-120 ease-tds',
    // pressed·hover overlay — 그림자가 아니라 fill 위에 얹는 반투명 막
    'after:pointer-events-none after:absolute after:inset-0 after:-z-10 after:rounded-[inherit] after:transition-colors after:duration-120',
    'hover:after:bg-hover active:after:bg-press',
    'focus-visible:outline-2 focus-visible:outline-offset-2',
    'disabled:pointer-events-none disabled:opacity-30 aria-disabled:pointer-events-none aria-disabled:opacity-30',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-fill-brand text-fg-inverse',
        secondary: 'bg-fill-secondary text-fg-primary',
        danger: 'bg-fill-danger text-fg-inverse',
        // 텍스트 링크에 가까운 약한 위계 — press overlay 대신 옅은 회색 면
        ghost:
          'bg-transparent text-fg-brand hover:after:bg-fill-secondary active:after:bg-fill-tertiary',
      },
      size: {
        xl: 'h-14 rounded-xl px-7 text-label-l [&_svg]:size-5',
        l: 'h-12 rounded-l px-6 text-label-l [&_svg]:size-5',
        m: 'h-10 rounded-m px-4 text-label-m [&_svg]:size-4',
        s: 'h-8 rounded-[10px] px-3 text-label-s [&_svg]:size-4',
        icon: 'size-10 rounded-m [&_svg]:size-5',
      },
      block: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'm',
    },
  },
);

type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    /** 자식 요소(Link 등)에 버튼 스타일을 입힌다 */
    asChild?: boolean;
    /** 라벨을 숨기고 3-dot 을 보여 준다 — 누르지 못하게 막는다 */
    loading?: boolean;
  };

export function Button({
  className,
  variant,
  size,
  block,
  asChild = false,
  loading = false,
  disabled,
  children,
  type,
  ...props
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size, block }), className);

  if (asChild) {
    return (
      <Slot.Root className={classes} {...props}>
        {children}
      </Slot.Root>
    );
  }

  return (
    <button
      type={type ?? 'button'}
      className={classes}

      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      {...props}
    >
      <span
        className={cn(
          'inline-flex items-center gap-1.5 transition-opacity duration-120',
          loading && 'opacity-0',
        )}
      >
        {children}
      </span>
      {loading ? <Dots className='absolute' /> : null}
    </button>
  );
}
